import React from 'react';
import { ChevronRight } from 'lucide-react';
import { LIFECYCLE_STAGES } from '../../data/lifecycleStages';

// Combined silhouette for the desktop journey bar: an 820x94 pill (bar) with a
// rounded bubble extending out of the top-centre, drawn as one continuous path.
// Combined silhouette for the desktop journey bar: an 820x120 bar with pill ends
// (same radius as the hero search bar) and a bubble on the top-centre, drawn as one
// continuous path. Every corner shares the SAME 30px radius - the bar's pill caps,
// the bubble's two top corners, and the two connection joins (concave quarter-circles).
const JOURNEY_SHAPE =
  'M30,60 L220,60 A30,30 0 0 0 250,30 A30,30 0 0 1 280,0 L540,0 A30,30 0 0 1 570,30 A30,30 0 0 0 600,60 L790,60 A30,30 0 0 1 790,120 L30,120 A30,30 0 0 1 30,60 Z';

/**
 * LifecycleStagePicker - five pills under the homepage hero.
 *
 * Each pill opens the canonical LifecycleStageModal for that stage.
 * Sits in place of (or above) the existing 6-tile JourneyChips. The
 * journey chips component is left intact so we can swap back in one
 * line if needed.
 *
 *   tone="light"  - for dark/video hero backgrounds (default)
 *   tone="dark"   - for light page backgrounds
 *
 * Props
 *   onSelectStage(stage)  - parent supplies the handler; parent owns
 *                           the active-stage state and renders the modal.
 *   showSkip              - when true, render a small "Skip - I know
 *                           what I want" link below the pills that
 *                           reveals the original JourneyChips (power-
 *                           user escape hatch).
 *   onSkip                - skip handler.
 */
const LifecycleStagePicker = ({
  tone = 'light',
  onSelectStage,
  showSkip = true,
  onSkip,
  className = '',
}) => {
  const pillBase =
    tone === 'light'
      ? 'bg-slate-900/40 hover:bg-slate-900/55 text-white border-white/30 hover:border-white/60 [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]'
      : 'bg-white hover:bg-gray-50 text-[#1d1d1f] border-gray-200 hover:border-[#1d1d1f]';

  const skipBase =
    tone === 'light'
      ? 'text-white/90 hover:text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.55)]'
      : 'text-gray-500 hover:text-gray-900';

  // Desktop "connected journey bar" styling (lg+)
  const trackBase =
    tone === 'light'
      ? 'border-white/20 bg-white/10'
      : 'border-gray-200 bg-white/70';
  const segmentBase =
    tone === 'light'
      ? 'text-white hover:bg-white/20'
      : 'text-[#1d1d1f] hover:bg-gray-100';
  const chevronBase = tone === 'light' ? 'text-white/70' : 'text-gray-400';

  return (
    <div className={className} data-testid="lifecycle-stage-picker">
      {/* Label shown above the pills on smaller screens only */}
      <p
        className={`xl:hidden text-[12px] sm:text-[14px] font-normal mb-2.5 sm:mb-3 ${
          tone === 'light' ? 'text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.5)]' : 'text-gray-600'
        }`}
      >
        Where are you in your journey?
      </p>

      {/* Desktop (xl+): one continuous shape - a search-bar-style pill with a
          bubble extending out of the top-centre, drawn as a single outline. */}
      <div className="hidden xl:flex justify-center" data-testid="lifecycle-journey-bar">
        <div className="relative w-[820px] h-[120px] mt-8">
          {/* single translucent fill clipped to the combined silhouette */}
          <div
            className="absolute inset-0 backdrop-blur-md"
            style={{
              clipPath: `path('${JOURNEY_SHAPE}')`,
              background: tone === 'light' ? 'rgba(15,23,42,0.45)' : 'rgba(255,255,255,0.72)',
              filter: 'drop-shadow(0 12px 30px rgba(0,0,0,0.18))',
            }}
          />
          {/* single outline stroke on top of the fill */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 820 120"
            fill="none"
            aria-hidden="true"
          >
            <path
              d={JOURNEY_SHAPE}
              fill="none"
              stroke={tone === 'light' ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.08)'}
              strokeWidth="1"
            />
          </svg>

          {/* question, inside the top bubble */}
          <div
            className={`absolute top-0 left-1/2 -translate-x-1/2 h-[60px] flex items-center px-5 text-[14px] font-medium ${
              tone === 'light' ? 'text-white' : 'text-gray-700'
            }`}
          >
            Where are you in your journey?
          </div>

          {/* stages, inside the bar */}
          <div className="absolute left-0 right-0 bottom-0 h-[60px] flex items-center justify-between pl-4 pr-3">
            {LIFECYCLE_STAGES.map((stage, i) => (
              <React.Fragment key={stage.slug}>
                <button
                  type="button"
                  onClick={() => onSelectStage && onSelectStage(stage)}
                  className={`inline-flex items-center gap-1.5 rounded-full pl-1.5 pr-2.5 py-1.5 text-[13.5px] font-semibold whitespace-nowrap transition duration-200 hover:scale-[1.05] active:scale-100 cursor-pointer ${segmentBase}`}
                  data-testid={`lifecycle-stage-bar-${stage.slug}`}
                  aria-label={`${stage.name} stage`}
                >
                  <img src={stage.iconSrc} alt="" aria-hidden="true" className="w-8 h-8 object-contain shrink-0" />
                  <span>{stage.name}</span>
                </button>
                {i < LIFECYCLE_STAGES.length - 1 && (
                  <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${chevronBase}`} aria-hidden="true" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile, tablet & small desktop: wrapped pills (unchanged) */}
      <div className="xl:hidden mx-auto flex flex-wrap items-center justify-center gap-2 sm:gap-3 max-w-5xl">
        {LIFECYCLE_STAGES.map((stage) => (
          <button
            key={stage.slug}
            type="button"
            onClick={() => onSelectStage && onSelectStage(stage)}
            className={`inline-flex items-center justify-center min-h-[40px] px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-[12.5px] sm:text-[14px] font-semibold border backdrop-blur-sm transition duration-200 hover:scale-[1.05] active:scale-100 cursor-pointer ${pillBase}`}
            data-testid={`lifecycle-stage-${stage.slug}`}
            aria-label={`${stage.name} stage`}
          >
            <span>{stage.name}</span>
          </button>
        ))}
      </div>
      {showSkip && onSkip && (
        <button
          type="button"
          onClick={onSkip}
          className={`mt-3 sm:mt-3.5 text-[11.5px] sm:text-[12.5px] underline-offset-4 hover:underline transition-colors ${skipBase}`}
          data-testid="lifecycle-skip"
        >
          Skip - I know what I want
        </button>
      )}
    </div>
  );
};

export default LifecycleStagePicker;
